import { Dashboardv99990101CommonModels } from '@sequencehq/api/dashboard/v99990101'

import { CustomerSnapshot } from '../../types'
import { BeforeAfterComparison, ComparisonRow } from './BeforeAfterComparison'
import { ContactCard } from './ContactCard'

type CustomerContact = Dashboardv99990101CommonModels.Customers.CustomerContact

// How far an off-focus row rests from centre for the fade/slide — small, so it
// reads as a nudge rather than a full slide.
const ROW_SLIDE_OFFSET = '28px'

/**
 * Before/after comparison of the customer's billing contacts, shown beneath the
 * customer-info comparison in the review modal preview. One row per contact
 * (the union of before and after): a removed contact leaves its `after` cell
 * empty, an added one leaves its `before` cell empty, and unchanged contacts
 * appear identically in both columns. Only the focused contact's row shows at a
 * time (the rest fade out); with none focused yet the first contact's row shows,
 * so a card is always visible. The "No billing contact" placeholder is reserved
 * for the case where a whole side has no contacts at all (so an empty cell next
 * to other contacts reads as "this row's contact was added/removed", not "no
 * contacts"). Renders nothing only when the customer has no contacts on either
 * side.
 */
export const BillingContactComparison = ({
  before,
  after,
  activeContactId
}: {
  before: CustomerSnapshot
  after: CustomerSnapshot
  /** The contact whose form is currently focused — its `after` card is ringed. */
  activeContactId?: string
}) => {
  const rows = buildContactRows(before, after, activeContactId)

  if (rows.length === 0) {
    return null
  }

  return (
    <BeforeAfterComparison
      data-testid="customerInfoReview.contactComparison"
      rows={rows}
    />
  )
}

const buildContactRows = (
  before: CustomerSnapshot,
  after: CustomerSnapshot,
  activeContactId?: string
): ComparisonRow[] => {
  const beforeById = indexById(before.contacts)
  const afterById = indexById(after.contacts)
  const contactIds = [...new Set([...beforeById.keys(), ...afterById.keys()])]

  // The placeholder means "no billing contacts", so it only applies when a side
  // is entirely empty; an empty cell next to other contacts is left blank.
  const beforeHasNoContacts = beforeById.size === 0
  const afterHasNoContacts = afterById.size === 0

  // Row to preview: the focused contact, or the first row when none is focused
  // yet (on step entry, before autofocus), so a card is always shown.
  const matchedIndex =
    activeContactId !== undefined ? contactIds.indexOf(activeContactId) : -1
  const activeIndex = matchedIndex === -1 ? 0 : matchedIndex

  return contactIds.map((id, index) => {
    // An added contact has no "before" counterpart, so it shows as a single
    // centred card labelled "New contact" rather than a before/after pair.
    const isNew = !beforeById.has(id)
    const isActive = index === activeIndex

    return {
      key: id,
      'data-testid': `customerInfoReview.contactRow.${id}`,
      // Only the active row is shown; the rest fade out so switching focus fades
      // the new row in rather than others flashing.
      dimmed: !isActive,
      // Off-focus rows rest above/below centre by direction, so switching focus
      // slides the new row in from that side.
      offsetY: isActive
        ? '0px'
        : index > activeIndex
          ? ROW_SLIDE_OFFSET
          : `-${ROW_SLIDE_OFFSET}`,
      singleLabel: isNew ? 'New contact' : undefined,
      before: isNew ? null : (
        <ContactCard
          contact={beforeById.get(id)}
          variant="before"
          companyName={before.legalName}
          showEmptyPlaceholder={beforeHasNoContacts}
        />
      ),
      after: (
        <ContactCard
          contact={afterById.get(id)}
          variant="after"
          companyName={after.legalName}
          showEmptyPlaceholder={afterHasNoContacts}
          highlighted={isActive}
          data-testid={`customerInfoReview.contactCard.after.${id}`}
        />
      )
    }
  })
}

const indexById = (
  contacts: CustomerContact[] | undefined
): Map<string, CustomerContact> =>
  new Map((contacts ?? []).map(contact => [contact.id, contact]))
